// Weather per wind farm, hour by hour: live DWD forecast (MOSMIX via Bright Sky) or a built-in example scenario.
// Everything is converted to hub height: wind (power law), temperature, pressure, air density, icing risk.
import { DEG, clamp, rng, hashStr, gauss } from './util.js';
import { FLEET, TURB } from './content.js';

export var H = 96;                 // hours modelled (the charts show 72)
export var T0 = 0;                 // start of hour 0 (ms)
export var WX = {};                // farm id -> { src, hours: [...] }
export var WXS = {};               // weather point id -> { src, hours } (towns, for the weather map only)
export var STATIONS = [];          // weather points: { id, name, lat, lon, elev, alpha, coast }
export var LIVE = { ok: false, at: null, failed: [] };

function stations() {
  if (!STATIONS.length && FLEET.weatherPoints) STATIONS = FLEET.weatherPoints.points.map(function (p) { return { id: 'wp-' + p[0], name: p[0], lat: p[1], lon: p[2], elev: p[3], alpha: 0.18, coast: p[1] > 53.5 && p[3] < 30 }; });
  return STATIONS;
}
export function setStart(ms) { var d = new Date(ms); d.setMinutes(0, 0, 0); T0 = d.getTime(); }
export function hourDate(i) { return new Date(T0 + i * 3600000); }

// ---------- live forecast ----------
function pad(n) { return (n < 10 ? '0' : '') + n; }
function localIso(ms) { var d = new Date(ms); return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) + 'T' + pad(d.getHours()) + ':00'; }

export function loadLive(base, timeoutMs) {
  var url0 = base || 'https://api.brightsky.dev/weather';
  var jobs = FLEET.farms.concat(stations()).map(function (f) {
    var u = url0 + '?lat=' + f.lat + '&lon=' + f.lon + '&date=' + encodeURIComponent(localIso(T0)) + '&last_date=' + encodeURIComponent(localIso(T0 + H * 3600000)) + '&tz=Europe%2FBerlin';
    var ctl = typeof AbortController !== 'undefined' ? new AbortController() : null, timer = setTimeout(function () { if (ctl) ctl.abort(); }, timeoutMs || 8000);
    return fetch(u, ctl ? { signal: ctl.signal } : {}).then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (j) { clearTimeout(timer); return { id: f.id, rows: j.weather || [], sources: j.sources || [] }; })
      .catch(function (e) { clearTimeout(timer); return { id: f.id, err: String(e) }; });
  });
  return Promise.all(jobs).then(function (res) {
    var raw = {}, failed = [];
    res.forEach(function (r) { if (r.err || r.rows.length < 24) { if (r.id.indexOf('wp-') !== 0) failed.push(r.id); } else raw[r.id] = r; });
    LIVE.failed = failed; LIVE.ok = failed.length < FLEET.farms.length; LIVE.at = new Date();
    return raw;
  });
}

// Magnus formula: relative humidity from temperature and dew point
function rhFrom(t, td) { if (t == null || td == null) return null; var a = 17.625, b = 243.04; return clamp(100 * Math.exp(a * td / (b + td) - a * t / (b + t)), 1, 100); }

function fromLive(f, raw) {
  var rows = raw.rows, hours = [], byIdx = {};
  rows.forEach(function (w) { var i = Math.round((Date.parse(w.timestamp) - T0) / 3600000); if (i >= 0 && i < H) byIdx[i] = w; });
  var last = null, src = raw.sources;
  for (var i = 0; i < H; i++) {
    var w = byIdx[i] || last; if (!w) { for (var k = i; k < H; k++) if (byIdx[k]) { w = byIdx[k]; break; } }
    last = w;
    var rh = w.relative_humidity != null ? w.relative_humidity : rhFrom(w.temperature, w.dew_point);
    hours.push({ v10: (w.wind_speed || 0) / 3.6, g10: (w.wind_gust_speed != null ? w.wind_gust_speed : (w.wind_speed || 0) * 1.5) / 3.6, dir: w.wind_direction != null ? w.wind_direction : 250,
      t2: w.temperature != null ? w.temperature : 10, rh: rh != null ? rh : 80, p: w.pressure_msl || 1013, vis: w.visibility != null ? w.visibility : 20000,
      cloud: w.cloud_cover != null ? w.cloud_cover : 60, cond: w.condition || 'dry', rain: w.precipitation || 0 });
  }
  var station = src && src.length ? src.filter(function (s) { return s.observation_type === 'forecast'; })[0] || src[0] : null;
  return { src: 'live', station: station ? station.station_name : '', hours: hours };
}

// ---------- example scenarios ----------
// Large-scale pressure pattern per scenario: a low (counter-clockwise, blowing slightly inwards) or a high (clockwise,
// blowing slightly outwards) whose centre moves during the 72 hours. It gives every place its own wind direction.
var SYN = {
  typical: { type: 'low', a: [57.2, 1], b: [58.5, 15], inflow: 25 },     // autumn low over the North Sea moving to Scandinavia
  storm: { type: 'low', a: [55.8, -5], b: [56.8, 21], inflow: 25 },      // storm low crossing the North Sea and Denmark
  holiday: { type: 'high', a: [52.8, 17.5], b: [52.2, 19.5], inflow: 20 }, // high over Poland: southerly to south-easterly flow
  frost: { type: 'high', a: [51.6, 10.2], b: [51.2, 12.2], inflow: 15 }   // high right over Germany: light winds turning around it
};
function synDir(scn, f, i) {
  var S = SYN[scn] || SYN.typical, k = Math.min(1, i / 71), clat = S.a[0] + (S.b[0] - S.a[0]) * k, clon = S.a[1] + (S.b[1] - S.a[1]) * k;
  var dx = (f.lon - clon) * Math.cos(f.lat * DEG), dy = f.lat - clat, r = Math.sqrt(dx * dx + dy * dy) || 1e-6, ux = dx / r, uy = dy / r;
  var tx = S.type === 'low' ? -uy : uy, ty = S.type === 'low' ? ux : -ux, a = S.inflow * DEG, sg = S.type === 'low' ? -1 : 1;
  var wx = Math.cos(a) * tx + sg * Math.sin(a) * ux, wy = Math.cos(a) * ty + sg * Math.sin(a) * uy;
  return (Math.atan2(wx, wy) / DEG + 180 + 360) % 360;   // meteorological: the direction the wind comes from
}

function hod(i) { return hourDate(i).getHours(); }
function synth(scn, f) {
  var d = new Date(T0), r = rng(hashStr(scn + f.id + d.getFullYear() + d.getMonth() + d.getDate())), north = clamp((f.lat - 50) / 4.6, 0, 1);
  var fac = Math.pow(TURB.hubM / 10, f.alpha), hours = [], n = 0, phase = r() * 30, delay = (f.lon - 7) * 1.4 + (54.6 - f.lat) * 1.2;
  var hill = f.elev > 400 ? 1 : 0;
  for (var i = 0; i < H; i++) {
    n = 0.82 * n + 0.55 * gauss(r);
    var h = hod(i), di = Math.cos(2 * Math.PI * (h - 15) / 24), v, g, dir, t, rh, p, vis = 20000, cloud = 60, cond = 'dry', rain = 0;
    if (scn === 'storm') {
      var x = (i - 30 - delay) / 9, bump = Math.exp(-x * x);
      v = 7 + 4 * north + (13 + 12 * north + 3 * hill) * bump + 0.9 * n;
      g = v * (1.24 + 0.08 * r()) + 2 * bump;
      dir = synDir('storm', f, i) + 4 * n;
      t = 11 - 4 * clamp((i - 30 - delay) / 12, 0, 1) + 1.5 * di - 3.5 * hill * 0.65;
      rh = 78 + 16 * bump; p = 1004 - (24 + 12 * north) * bump; cloud = 70 + 30 * bump; cond = bump > 0.35 ? 'rain' : 'dry'; rain = bump > 0.35 ? 2 * bump : 0; vis = 20000 - 14000 * bump;
    } else if (scn === 'holiday') {
      // sea-breeze-like daily cycle: the wind peaks in the early afternoon, together with the sun
      v = 8.8 + 2.4 * north + 2.6 * di + 1.2 * Math.sin(2 * Math.PI * (i + phase) / 40) + 0.7 * n + 1.2 * hill;
      g = v * 1.32; dir = synDir('holiday', f, i) + 4 * n;
      t = 13 + 5 * di - 3 * hill; rh = 62 - 12 * di; p = 1016; cloud = 12; vis = 40000;
    } else if (scn === 'frost') {
      // high pressure with an inversion: freezing fog in the inland lowlands at night and in the morning,
      // the hills in cloud with rime ice, the coast mild and clear of ice
      v = 4.8 + 2.4 * north + 1.1 * Math.sin(2 * Math.PI * (i + phase) / 26) + 0.4 * n + 1.8 * hill;
      g = v * 1.4; dir = synDir('frost', f, i) + 8 * n; p = 1031;
      var foggy = hill ? i % 24 < 20 : !f.coast && (di < 0.3 || (i > 30 && i < 44));
      if (f.coast) { t = 3.4 + 1.4 * di + 0.4 * n; rh = 88 + 4 * Math.sin(i / 5); cloud = 70; vis = 9000; }
      else if (hill) { t = -2.6 + 1.2 * di + 0.4 * n; rh = foggy ? 99 : 90; cloud = 100; vis = foggy ? 150 : 3000; cond = foggy ? 'fog' : 'dry'; }
      else { t = -0.4 + 2.6 * di + 0.4 * n; rh = foggy ? 98 : 86; cloud = foggy ? 100 : 40; vis = foggy ? 300 : 6000; cond = foggy ? 'fog' : 'dry'; }
    } else {   // typical autumn days: used when the live forecast cannot be reached
      v = 6.3 + 3.4 * north + 2.6 * Math.sin(2 * Math.PI * (i + phase) / 31) + 0.6 * di + 0.9 * n + 1.2 * hill;
      g = v * 1.45; dir = synDir('typical', f, i) + 5 * n;
      t = 9 + 4 * di - 3 * hill; rh = 82 - 12 * di; p = 1012 + 6 * Math.sin(2 * Math.PI * i / 70); cloud = 65;
    }
    v = Math.max(0.3, v); g = Math.max(v * 1.1, g);
    hours.push({ v10: v / fac, g10: g / Math.pow(TURB.hubM / 10, f.alpha * 0.6), dir: (dir + 360) % 360, t2: t, rh: clamp(rh, 20, 100), p: p, vis: vis, cloud: cloud, cond: cond, rain: rain });
  }
  return { src: scn === 'live' ? 'typical' : scn, station: '', hours: hours };
}

// ---------- hub height ----------
// saturation vapour pressure (hPa), Tetens
function es(t) { return 6.1078 * Math.pow(10, 7.5 * t / (t + 237.3)); }
function sunElev(i, f) {
  var d = hourDate(i), jd = d.getTime() / 86400000 + 2440587.5, n = jd - 2451545, L = (280.46 + 0.9856474 * n) % 360, g = (357.528 + 0.9856003 * n) * DEG;
  var lam = (L + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g)) * DEG, eps = 23.439 * DEG, dec = Math.asin(Math.sin(eps) * Math.sin(lam));
  var utcH = d.getUTCHours() + d.getUTCMinutes() / 60, ha = ((utcH - 12) * 15 + f.lon) * DEG;
  return Math.asin(Math.sin(f.lat * DEG) * Math.sin(dec) + Math.cos(f.lat * DEG) * Math.cos(dec) * Math.cos(ha)) / DEG;
}

function finish(f, w) {
  var fac = Math.pow(TURB.hubM / 10, f.alpha), gfac = Math.pow(TURB.hubM / 10, f.alpha * 0.6), z = f.elev + TURB.hubM;
  w.hours.forEach(function (x, i) {
    x.v = x.v10 * fac;
    x.g = Math.max(x.v * 1.1, x.g10 * gfac);
    x.t = x.t2 - 0.0065 * TURB.hubM;                                  // temperature at hub height
    var pz = x.p * Math.exp(-z / 8434), pv = x.rh / 100 * es(x.t), T = x.t + 273.15;
    x.pz = pz;
    x.rho = ((pz - pv) * 100) / (287.05 * T) + (pv * 100) / (461.5 * T);   // moist air density, kg/m³
    var fog = x.vis < 1000 || x.cond === 'fog', wetPrecip = x.rain > 0 && x.t < 1;
    var accrete = x.t <= 1 && x.t >= -10 && (fog || wetPrecip || x.rh >= 95) ? clamp(0.35 + (x.rh - 90) / 20 + (fog ? 0.25 : 0) + (wetPrecip ? 0.2 : 0), 0, 1) : 0;
    // ice stays on the blades after the fog lifts and melts slowly above 0 °C
    var prev = i ? w.hours[i - 1].ice : 0;
    x.ice = Math.max(accrete, prev * (x.t < 0 ? 0.93 : x.t < 2 ? 0.75 : 0.4));
    x.sun = Math.max(0, Math.sin(sunElev(i, f) * DEG));
  });
  return w;
}

export function buildWeather(scn, raw) {
  WX = {};
  FLEET.farms.forEach(function (f) {
    var w = scn === 'live' && raw && raw[f.id] ? fromLive(f, raw[f.id]) : synth(scn, f);
    WX[f.id] = finish(f, w);
  });
  WXS = {};
  stations().forEach(function (p) { WXS[p.id] = finish(p, scn === 'live' && raw && raw[p.id] ? fromLive(p, raw[p.id]) : synth(scn, p)); });
  return WX;
}
